import Doms from "oc-commons/Doms.js";
import Maths3 from "./Maths3.js";
import OcAxisHelper from "./objects/OcAxisHelper.js";
import Scenes from "oc-threejs/Scenes.js";
import Textures from "oc-threejs/Textures.js";
import UniversalLoader from "oc-threejs/loader/UniversalLoader.js";
import Screens from "./Screens";
import {v3, quat} from "oc-threejs/short3";
import OcMaterialHelper from "./objects/OcMaterialHelper";
import Commons from 'oc-commons/Commons.js';

/**
 * Statische Utility-Klasse zum Debuggen von Threejs Szenen.
 * Debugs wird auch global im Window-Context veröffentlicht, um auf der Console erreichbar zu sein.
 */
export default class Debugs {

    static objectsToClean = [];
    static _debugObjectsIn2RenderPass = false;

    static cleanObjects() {
        Debugs.objectsToClean.forEach(object => Scenes.remove(object));
    }

    static setDebugObjectsIn2RenderPass(on=true) {
        Debugs._debugObjectsIn2RenderPass = on;
        STAGE.setSecondRenderPass(on);
    }

    /**
     * Fügt ein Debug-Object zur Scene hinzu
     */
    static add(object) {
        object.raycastVisibleTags = [];
        if (Debugs._debugObjectsIn2RenderPass && STAGE.secondPassScene) {
            STAGE.secondPassScene.add(object);
        } else {
            STAGE.scene.add(object);
        }
        STAGE.dirty();
        return object;
    }

    /**
     * gibt das 3D-Object zurück
     * @param identifier kann eine ID sein, der Name oder das Objekt selbst
     */
    static resolve(identifier) {
        if (_.isNumber(identifier)) {
            return STAGE.scene.getObjectById(identifier, true);
        }
        if (_.isString(identifier)) {
            return STAGE.scene.getObjectByName(identifier, true);
        }
        if (identifier instanceof THREE.Object3D) {
            return identifier;
        }
        return null;
    }

    /**
     * Loggt den Objekt-Baum in einer auf/zuklappbaren Weise
     */
    static tree(root = null, onlyMainObjects = false) {
        root = Debugs.resolve(root || STAGE.scene);
        const iterate = (dumper, object) => {
            var dumpIt = true;
            if (onlyMainObjects) {
                if (object instanceof THREE.Light) {
                    dumpIt = false;
                }
                if (object instanceof THREE.Group) {
                    dumpIt = false;
                }
                if (object instanceof THREE.AxisHelper) {
                    dumpIt = false;
                }
                if (object.isDebugObject) {
                    dumpIt = false;
                }
                if (object.dependsOnObjectsArray && object.dependsOnObjectsArray.length > 0) {
                    dumpIt = false;
                }
            }
            if (dumpIt) {
                var subDumper = {
                    '_': object
                };
                dumper[object + ' [' + object.id + ']'] = subDumper;
                object.children.forEach(child => iterate(subDumper, child));
            }
        }
        var dumper = {};
        iterate(dumper, root);
        console.log(dumper);
    }

    /**
     * Lädt ein Modell
     * @param path
     * @param callback
     */
    static load(path, callback = () => {
    }) {
        if (!path.startsWith('http')) {
            path = APP.settings.wkfgContentBaseUrl + 'models/' + path;
        }
        UniversalLoader.loadModel(path, object => {
            Debugs.add(object);
            callback(object);
        }, error => console.error(error));
    }

    /**
     * Durchsucht den Objektbaum nach allen Objekten mit zu searchString passendem Namen und gibt diesen aus
     * @param searchRegexp
     * @param root
     * @return erstes gefundenes Objekt
     */
    static search(searchString, root = null) {
        const list = Debugs.searchAll(searchString,root);
        list.forEach(x => console.log(x));
        return _.first(list);
    }

    /**
     * Durchsucht den Objektbaum nach allen Objekten mit zu searchString passendem Namen und gibt sie alle zurück
     * @param searchRegexp
     * @param root
     */
    static searchAll(searchString, root = null) {
        return Scenes.filter(root || STAGE.scene, x => searchString.test(x.name));
    }

    /**
     * Macht ein Object oder eine Liste von Objekten sichtbar, zoomt heran und lässt es aufblinken
     */
    static show(identifier, zoomTo = true, highlight = true, direction) {
        let objects = [];
        if (_.isArray(identifier)) {
            objects = identifier;
        } else {
            let object = Debugs.resolve(identifier);
            if (!object) {
                throw "no object found: " + identifier;
            }
            objects = [object];
        }

        objects.forEach(object => object.visible = true);
        if (zoomTo && objects.length > 0) {
            if (direction) STAGE.zoomTo(objects, direction)
            else STAGE.zoomTo(objects);
        }
        let i = 0;
        const blinkChange = () => {
            STAGE.dirty();
            if (i > 9) {
                objects.forEach(object => Textures.unhighlight(object));
                return false;
            }
            const d = i++ % 2;
            if (d == 0) {
                objects.forEach(object => Textures.highlight(object, new THREE.MeshBasicMaterial({color: new THREE.Color(0xffff00)})));
            } else {
                objects.forEach(object => Textures.highlight(object, new THREE.MeshBasicMaterial({color: new THREE.Color(0x000000)})));
            }
            return true;
        };
        if (highlight) {
            blinkChange();
            const interval = window.setInterval(() => {
                if (!blinkChange()) {
                    window.clearInterval(interval);
                }
            }, 300);
        }
    }

    /**
     * gibt einen Text aus
     */
    static text(message, position = v3()) {
        const sprite = Textures.createTextSprite(message,{
            cameraIndependendSize: true,
            moveTextUpinHeightMultiplies: 1,
        });
        sprite.position.copy(position);
        return Debugs.add(sprite);
    }

    /**
     * Zeigt eine Position im Raum als kleine rote Kugel an
     */
    static point(position = v3(), color = 0xff0000, size = 0.02 * STAGE.mInUnit) {
        var geometry = new THREE.SphereBufferGeometry(size, 8, 8);
        var material = new THREE.MeshBasicMaterial({color: color});
        var sphere = new THREE.Mesh(geometry, material);
        sphere.position.copy(position);
        return Debugs.add(sphere);
    }

    /**
     * Zeigt Position und Orientierung eines Objektes an (folgt dem Objekt aber nicht)
     */
    static axis(objectOrMatrix, size = 1 * STAGE.mInUnit) {
        const axis = new OcAxisHelper(size);

        if (objectOrMatrix) {
            if (objectOrMatrix instanceof THREE.Matrix4) {
                axis.copyMatrix(objectOrMatrix);
            } else {
                axis.position.copy(objectOrMatrix.getWorldPosition());
                axis.quaternion.copy(objectOrMatrix.getWorldQuaternion(quat()));
                axis.scale.copy(objectOrMatrix.getWorldScale());
            }
        }

        return Debugs.add(axis);
    }

    /**
     * Zeigt die boundingBox als Wireframe an, die dem Objekt folgt (VirtualChild)
     */
    static boundingBox(object, color = 0xff0000, linewidth = 0.1) {
        if (object==null) {
            return null;
        }
        const container = new THREE.Object3D();
        let wf = null;
        container.name = 'wireframe_of_' + object.name;
        container.parentPseudo = object;
        container.raycastVisibleTags = [];
        container.dependsOnObjectsArray = [object];
        let lastSize = null;
        container.onBeforeRenderStage = () => {
            const bounds = Scenes.boundingBox(object);
            const size = bounds.getSize();
            const center = bounds.getCenter();
            if (!lastSize || !lastSize.equals(size)) {
                Scenes.remove(wf);
                const box = Scenes.box(size, {color});
                box.position.copy(center);
                wf = Scenes.wireframe(box, linewidth, color);
                lastSize = size;
                container.add(wf);
            }
            wf.position.copy(center);
        };
        Debugs.add(container);
        return container;
    }

    /**
     * Zeigt einen Ray an
     */
    static ray(ray, color = 0xff0000) {
        var length = ray.direction.length();
        var arrow = new THREE.ArrowHelper(ray.direction, ray.origin, length, color);
        return Debugs.add(arrow);
    }

    /**
     * Zeigt eine Linie an (Array von 2 Punkten oder THREE.Line3)
     */
    static line(pointsOrLine3, color = 0xff0000, linewidth = 1) {
        return Debugs.add(Scenes.line(pointsOrLine3, {color, linewidth}));
    }

    /**
     * Zeigt eine Box an
     */
    static box(box, color = 0xff0000, wireframe = false) {
        const size = box.getSize();
        var geometry = new THREE.BoxBufferGeometry(size.x, size.y, size.z);
        var material = new THREE.MeshLambertMaterial({color, wireframe});
        var cube = new THREE.Mesh(geometry, material);
        cube.position.copy(box.getCenter());
        return Debugs.add(cube);
    }

    /**
     * Zeigt eine Sphere an
     */
    static sphere(sphere, color = 0xff0000, wireframe = false, segments=8) {
        var geometry = new THREE.SphereBufferGeometry(sphere.radius, segments, segments);
        var material = new THREE.MeshBasicMaterial({color, wireframe});
        var mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(sphere.center);
        return Debugs.add(mesh);
    }

    /**
     * Zeigt eine Plane an
     */
    static plane(plane, color = 0xff0000, size = 10 * STAGE.mInUnit, step = 10) {
        var grid = new THREE.GridHelper(size, step, color, color);
        grid.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(Maths3.vector.y, plane.normal));
        grid.position.copy(plane.coplanarPoint());
        return Debugs.add(grid);
    }

    /**
     * Zeigt einen Cylinder an
     */
    static cylinder(cylinder, color = 0xff0000) {
        var cylinderFinite = isFinite(cylinder.height) ? cylinder : cylinder.clone().setHeight(100);
        var geometry = new THREE.CylinderGeometry(cylinderFinite.radius, cylinderFinite.radius, cylinderFinite.height, 20);
        var material = new THREE.MeshLambertMaterial({color});
        var mesh = new THREE.Mesh(geometry, material);
        mesh.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(Maths3.vector.y, cylinderFinite.direction));
        mesh.position.copy(cylinderFinite.center());
        return Debugs.add(mesh);
    }

    /**
     * Zeigt einen Cone an
     */
    static cone(cone, color = 0xff0000) {
        var coneFinite = isFinite(cone.height) ? cone : cone.clone().setHeight(100);
        var geometry = new THREE.CylinderGeometry(coneFinite.radiusBottom(), 0, coneFinite.height, 20);
        var material = new THREE.MeshLambertMaterial({color});
        var mesh = new THREE.Mesh(geometry, material);
        mesh.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(Maths3.vector.y, coneFinite.direction));
        mesh.position.copy(coneFinite.center());
        return Debugs.add(mesh);
    }

    /**
     * zeigt das Wireframe zu einem Objekt an
     */
    static wireframe(object, color = 0xff0000) {
        const material = new THREE.LineBasicMaterial({color});
        object.updateMatrixWorld(true);
        object.traverse(o => {
            if (o instanceof THREE.Mesh) {
                const wireframe = new THREE.WireframeGeometry(o.geometry);
                const line = new THREE.LineSegments(wireframe, material);
                line.copyMatrix(o.matrixWorld);
                Debugs.add(line);
            }
        });
    }

    /**
     * gibt den ScenenPath eines Objektes zurück (Parents*.toString() + '/' + Object.toString())
     * @returns {string}
     */
    static scenePath(object) {
        return (object.parent ? Debugs.scenePath(object.parent) + '/' : '') + object;
    }

    /**
     * Zeigt ein Image in einem neuen Window an. Das Image kann ein Image-Element,
     * einen JQuery.Selektor, ein Canvas, eine URL oder einen base64-enkodiertes Image sein.
     */
    static showImageInWindow(imageData) {
        const openWindow = (img) => {
            const newWindow = window.open("", "image", "scrollbars=0, toolbar=0, width=" + img.width + ", height=" + img.height, false);
            if (newWindow) {
                newWindow.document.write(img.outerHTML);
            } else {
                const div = $('<div style="position:absolute;z-index:1000;top:10px;left:10px;">');
                div.append(img);
                $('body').append(div);
            }
        }
        if (Doms.isJQueryObject(imageData)) {
            if (imageData.length === 0) {
                imageData = null;
            } else {
                imageData = imageData[0];
            }
        }
        if (_.isString(imageData)) {
            var myImage = new Image();
            myImage.src = imageData;
            myImage.style.border = 'none';
            myImage.style.outline = 'none';
            myImage.style.position = 'fixed';
            myImage.style.left = '0';
            myImage.style.top = '0';
            myImage.onload = () => {
                openWindow(myImage);
            }
        } else if (imageData != null) {
            if (imageData instanceof HTMLImageElement) {
                imageData.onload = () => {
                    openWindow(imageData);
                }
            } else {
                Debugs.showImageInWindow(imageData.toDataURL("image/jpeg"));
            }
        }
    }

    /**
     * stellt sicher, dass eine Zahl eine number ist, und springt sonst in den Debugger
     * @param number
     */
    static checkNaN(number) {
        if (Number.isNaN(number)) {
            console.warn('not a number', number);
            debugger;
            return 0;
        }
        return number;
    }

    /**
     * prüft diverse Fälle, ob und warum ein Objekt (inklusiv Kinder) eventuell nicht angezeigt wird,
     * und gibt die möglichen Gründe auf der Console aus. Behandelt werden Materialparameter wie
     * transparent und opacity, ob das Objekt in der Scene ist, ob seine Position im Screenspace ist,
     * wie seine sichtbare Größe ist, mögliche Verdeckung durch andere Objekte, visible-Flag und
     * Near/Far-Plane. Außerdem wird die BoundingBox um das Objekt rot angezeigt.
     * @returns {boolean}
     */
    static checkVisibility(object) {
        if (object == null) {
            console.warn('checkVisibility: object is null');
            return false;
        }
        if (!(object instanceof THREE.Object3D)) {
            console.warn('checkVisibility: object is no THREE.Object3D');
            return false;
        }
        if (Scenes.isOrphan(object)) {
            console.warn('checkVisibility: object is not in scene');
            return false;
        }
        if (!object.visible) {
            console.warn('checkVisibility: object is invisible');
            return false;
        }
        const screenPixelPos = Screens.screenPosition(object.getWorldPosition());
        if (screenPixelPos.z < 0) {
            console.warn('checkVisibility: object is '+screenPixelPos.z+' m behind camera');
            return false;
        }
        if (screenPixelPos.x < 0 || screenPixelPos.x > STAGE.canvas.width() || screenPixelPos.y < 0 || screenPixelPos.y > STAGE.canvas.height()) {
            console.warn('checkVisibility: object position is out of screen, screen position in pixel: '+screenPixelPos);
            return false;
        }

        const screenDistance = object.getWorldPosition().distanceTo(STAGE.camera.position);
        if (screenDistance > STAGE.camera.far) {
            console.warn('checkVisibility: object position is too far away (camera.far)');
            return false;
        }
        if (screenDistance < STAGE.camera.near) {
            console.warn('checkVisibility: object position is too near (camera.near)');
            return false;
        }
        const raycaster = new THREE.Raycaster();
        const screenUniPos = object.getWorldPosition().clone().project(STAGE.camera);
        raycaster.setFromCamera(screenUniPos, STAGE.camera);

        try {
            const intersects = raycaster.intersectObjects(STAGE.scene.children, true);
            const behinds = intersects.filter(i => i.distance <= screenDistance.z);
            if (behinds.length > 0) {
                console.warn('checkVisibility: object is maybe masked / behind the following objects', behinds);
                return false;
            }
        } catch (e) {
            console.warn(e);
        }
        const screenRadius = Screens.visibleSizeInPx(Scenes.boundingSphere(object));
        if (screenRadius < 5) {
            console.warn('checkVisibility: object is maybe too small, screen radius in px:', screenRadius);
            return false;
        }
        if (screenRadius > Math.min(STAGE.canvas.width(), STAGE.canvas.height())) {
            console.warn('checkVisibility: object is maybe too big, screen radius in px:', screenRadius);
            return false;
        }

        let countVisibles = 0;
        const invisibleReasons = [];
        const visibles = [];
        Scenes.traverseWhile(object, o => o.visible, o => o.___realVisible = true);
        object.traverse(o => {
            if (!o.___realVisible) {
                invisibleReasons.push([o, 'not visible (or parent not visible)']);
            } else if (!o.geometry) {
                invisibleReasons.push([o, 'no geometry']);
            } else {
                o.geometry.computeBoundingSphere();
                const materials = o.material == null ? [] : (_.isArray(o.material) ? o.material : [o.material]);
                if (o.geometry.boundingSphere.radius <= 0.01) {
                    invisibleReasons.push([o, 'geometry bounding sphere radius near zero', o.geometry.boundingSphere]);
                } else if (!o.material || !materials.some(m => m.visible)) {
                    invisibleReasons.push([o, 'no visible material']);
                } else if (materials.some(m => m.transparent && m.opacity < 0.2)) {
                    invisibleReasons.push([o, 'material may be too transparent']);
                } else {
                    // wir vertrauen mal der BoundingSphere nicht und machen das selbst. Kam beim Zuschneiden von
                    // Objekten vor!
                    const vertices = Scenes.getVertices(o);
                    const sphereVertices = new THREE.Sphere().setFromPoints(vertices);
                    if (vertices.length < 3) {
                        invisibleReasons.push([o, 'less than 3 vertices']);
                    } else if (sphereVertices.radius < 0.01) {
                        invisibleReasons.push([o, 'vertices sphere radius near zero (' + sphereVertices.radius + '), but at the same time geometry bounding sphere is of normal size (' + o.geometry.boundingSphere.radius + ')!']);
                    } else {
                        visibles.push(o);
                        countVisibles++;
                    }
                }
            }
        });
        if (countVisibles == 0) {
            console.warn('checkVisibility: no visible object or child:', invisibleReasons);
            return false;
        } else {
            console.log('visibles', visibles);
        }
        console.log('checkVisibility: object should be visible', object);
        if (!object.___virtualBoundingBox || Scenes.isOrphan(object.___virtualBoundingBox)) {
            object.___virtualBoundingBox = Debugs.boundingBox(object);
        }
        // visibles.forEach(o=>o.material = new THREE.MeshBasicMaterial({color:0xff0000}));
        return true;
    }

    /**
     * Überwacht eine Member-Variable eines Objektes auf Veränderungen und
     * springt bei jeder Wertezuweisung (nur bei Änderung des Wertes) in den Debugger
     */
    static watch(objectInstance, propertyName, checkCallback = null) {
        console.log('Debugs.watch', objectInstance, propertyName);
        const _propertyName = '__' + propertyName;
        objectInstance[_propertyName] = objectInstance[propertyName];
        Object.defineProperty(objectInstance, propertyName, {
            get: () => {
                return objectInstance[_propertyName];
            },
            set: (value) => {
                if (objectInstance[_propertyName] !== value && (checkCallback == null || checkCallback(value))) {
                    try {
                        throw new Error('Stacktrace');
                    } catch (e) {
                        console.log(propertyName, value);
                        console.log(e);
                    }
                    debugger;
                }
                objectInstance[_propertyName] = value;
            }
        });
    }

    /**
     * erlaubt das kapseln einer Funktion, so dass bei überschreiten einer maximalen Calltiefe der debugger anspringt
     * @param fun
     */
    static stackoverflow(fun, maxcalldepth = 100) {
        let stackdepth = 0;
        return (...args) => {
            stackdepth++;
            if (stackdepth > maxcalldepth) {
                debugger;
            }
            const result = fun(...args);
            stackdepth--;
            return result;
        };
    }

    /**
     * stellt eine Reihe von Kugeln verschiedener Materialien dar zur Eichung des Light-Setups
     */
    static addMaterialHelper() {
        const helper = new OcMaterialHelper(STAGE.mInUnit);
        STAGE.add(helper);
        STAGE.dirty();
        return helper;
    }

    /** */
    static showVertices(object) {
        const points = Scenes.getVertices(object);
        const g = new THREE.Group();
        points.forEach(v => g.add(Debugs.point(v)));
        STAGE.scene.add(g);
        Debugs.show(g);
        return g;
    }

    //
    // static checkFaceOnClick({object, type} = {object: STAGE.scene, type: 'tap'}) {
    //     const intersection = Screens.intersectionAtMouse(event, selectableMeshs);
    //
    // }

    /**
     * Prüft, warum ein 3D-Objekt nicht tap oder drag - empfängt
     */
    static checkClick({object, type} = {object: STAGE.scene, type: 'tap'}) {
        console.log('checkClick: ein Klick-Listener wird registriert, der ausgibt, welches 3D-Objekt Mouse-Events erhält', 'event type=', type, 'root=', object);
        console.log('zuerst werden alle Objekte ausgeblendet, die nicht für ', type, ' empfänglich sind - eventuell muss man die raycastVisibleTags des Objektes anpassen');
        console.log('(also object3d.raycastVisibleTags=["' + type + '"] oder object3d.raycastVisibleTags=undefined, beides matched)');
        console.log('klicke auf das gewünschte Objekt - danach wird checkClick wieder unregistriert');

        if (APP.interactionHandler) {
            APP.interactionHandler.locked = true;
        }

        let invisibles = [];
        object.traverse((o) => {
            if (o.raycastVisibleTags != null) {
                invisibles.push([o, o.visible]);
                o.visible = o.raycastVisibleTags.includes(type);
            }
        });

        const revert = () => {
            invisibles.forEach(([o, oldVisible]) => {
                o.visible = oldVisible;
            });
            STAGE.canvas.off('click', click);
            STAGE.dirty();
            if (APP.interactionHandler) {
                APP.interactionHandler.locked = false;
            }
        }

        const click = (event) => {
            const intersection = Screens.intersectionAtMouse(event, object);
            const child = intersection?.object;
            if (child == null) {
                console.log('checkClick kein 3D-Objekt mit dem Mouse-Ray getroffen!');
            } else {

                Debugs.showFace(intersection.face,intersection.object);

                console.log('checkClick erstes vom Mouse-Ray getroffenes Objekt:');
                console.log(child);
                console.log('die exakte Intersection ist:');
                console.log(intersection)
                const propName = type == 'tap' ? 'tapable' : 'draggable';
                const root = Scenes.findUp(child, o => o[propName] == true);
                if (root == null) {
                    console.log('allerdings ist ', propName, '!=true für dieses Objekt oder einen seiner Parents, deswegen wird kein Objekt gefunden!');
                } else {
                    if (root === child) {
                        console.log('es ist selbst auch ', propName, '==true und müsste zurückgegeben werden!');
                    } else {
                        console.log('es ist allerdings selbst nicht ', propName, '==true, das gilt aber für folgenden Parent, der zurückgegeben werden müsste:');
                        console.log(root);
                    }
                    Debugs.boundingBox(root);
                }
            }
            revert();
        };

        STAGE.canvas.on('click', click);
        STAGE.dirty();
    }

    /** */
    static lightHelper(light, size=0.2) {
        let helper = null;
        let typeName = null;
        if (light instanceof THREE.AmbientLight) {
            typeName = "AmbientLight";
        } else if (light instanceof THREE.DirectionalLight) {
            typeName = "DirectionalLight";
        } else if (light instanceof THREE.HemisphereLight) {
            typeName = "HemisphereLight";
        } else if (light instanceof THREE.PointLight) {
            typeName = "PointLight";
        } else if (light instanceof THREE.RectAreaLight) {
            typeName = "RectAreaLight";
        } else if (light instanceof THREE.SpotLight) {
            typeName = "SpotLight";
        }
        if (light instanceof THREE.AmbientLight) {
            helper = new THREE.Group();
        } else if (light instanceof THREE.DirectionalLight) {
            helper = new THREE.DirectionalLightHelper(light,size);
        } else if (light instanceof THREE.HemisphereLight) {
            helper = new THREE.HemisphereLightHelper(light,size);
        } else if (light instanceof THREE.PointLight) {
            helper = new THREE.PointLightHelper(light,size);
        } else if (light instanceof THREE.RectAreaLight) {
            helper = new THREE.RectAreaLightHelper(light);
        } else if (light instanceof THREE.SpotLight) {
            helper = new THREE.SpotLightHelper(light);
        }
        if (helper) {
            if (light instanceof THREE.RectAreaLight) {
                light.add(helper);
            } else {
                helper.dependsOnObjectsArray = [light];
                STAGE.scene.add(helper);
            }
            if (helper.update) {
                helper.onBeforeRenderStage = ()=>helper.update();
            }
            helper.name = light.name+'Helper';
            if (!Commons.isEmpty(light.name)) {
                helper.add(Debugs.text(light.name+'\n'+typeName));
            }
        }
        return helper;
    }

    /** */
    static showFaceNormals(object, length=0.1, color=0xff0000) {
        const helper = new THREE.Group();
        const material = new THREE.LineBasicMaterial({color});
        Scenes.traverseFaces(object, (face,index,g,o)=>{
            const v = i=>g.vertices[i];
            const a = Maths3.middle([v(face.a),v(face.b),v(face.c)]);
            const b = face.normal.clone().multiplyScalar(length).add(a);
            const geometry = new THREE.BufferGeometry().setFromPoints( [a,b].map(o=>object.localToWorld(o)) );
            const line = new THREE.Line( geometry, material );
            helper.add(line);
        });
        return Debugs.add(helper);
    }

    /** */
    static showFace(face,object) {
        const helper = new THREE.Group();
        const material = new THREE.LineBasicMaterial({color:0xffff00});

        let geometry = object.geometry;
        if (geometry == null) {
            return;
        }
        if (geometry instanceof THREE.BufferGeometry) {
            geometry = new THREE.Geometry().fromBufferGeometry(geometry);
        }

        const v = i=>geometry.vertices[i];
        const w = p=>object.localToWorld(p);

        (()=>{
            const a = Maths3.middle([v(face.a),v(face.b),v(face.c)]);
            const b = face.normal.clone().multiplyScalar(length).add(a);
            const line1 = new THREE.Line( new THREE.BufferGeometry().setFromPoints( [a,b].map(w) ), material );
            helper.add(line1);
        })();

        (()=>{
            const p = [v(face.a),v(face.b),v(face.c)].map(w);
            p.forEach((x,i)=>helper.add(Debugs.point(x,[0xff0000,0x00ff00,0x0000ff][i])));

            const line2 = new THREE.Line( new THREE.BufferGeometry().setFromPoints(p), material );
            helper.add(line2);
        })();

        return Debugs.add(helper);
    }


    /**
     * prüft die Konsistenz des Scene-Baums, also ob ein child den falschen parent gesetzt hat
     */
    static checkParentChild3d(parent=STAGE.scene) {
        if (parent?.children) {
            for (let i = 0, l = parent.children.length; i < l; i++) {
                const child = parent.children[i];
                if (child.parent!==parent) {
                    console.log('scene tree inconsistency found: parent has child but child.parent!==parent',{parent,child});
                    throw new Error('scene tree inconsistency found: parent has child but child.parent!==parent');
                }
                Debugs.checkParentChild3d(child);
            }
        }
    }

}

window['Debugs'] = Debugs;



